import { useStoreState } from '@ariakit/react'
import * as React from 'react'
import styled from 'styled-components'

import { useTranslation } from '../../../../../app/_providers/TranslationProvider.client'
import { useEmbroidery } from '../../../../../containers/EmbroideryContainer/EmbroideryProvider'
import { useEmbroideryForm } from '../../../../../containers/EmbroideryContainer/useEmbroideryForm'
import { EmbroideryTextInput } from '../../../Modal/primitives/EmbroideryTextInput/EmbroideryTextInput'
import { ColorSelector } from '../../../Modal/primitives/selectors/ColorSelector/ColorSelector'
import {
  EmbroideryOptionWrapper,
  FormBody,
  Label,
} from '../../../Modal/primitives/shared-form-styles'
import { SubmissionButtons } from '../../../Modal/primitives/SubmissionButtons/SubmissionButtons'
import { useValidPlacementOptionsForLegacyFlow } from '../../../unorganized'
import { StyledHr } from '../DTCLogoEmbroideryForm/EmbroideryLogoSetup/styles'
import { IconPreviewItem } from '../PreviewItems/IconPreviewItem'

import { IconPlacement } from './FormBody/IconPlacement'
import { IconSelectorForm } from './FormBody/IconSelectorForm'
import { useValidationSchema } from './useValidationSchema'

export const IconEmbroideryForm = () => {
  const {
    productEmbroideryConfig,
    selectedFormKind,
    embroideryPriceRangeByType,
    withPricing,
    productColorHex,
  } = useEmbroidery()

  const initialsMaxChars = productEmbroideryConfig?.options.icon?.characterLimit ?? 2
  const validationSchema = useValidationSchema({ initialsMaxChars })
  const { form, resetForm } = useEmbroideryForm('ICON', {
    validationSchema,
    createEmbroideryOptions: values => ({
      iconDesignFile: values.iconImage?.designFile || undefined,
      iconDesignName: values.iconImage?.image.label || undefined,
      iconImageSource: values.iconImage?.image.src || undefined,
      iconInitials: values.initials || undefined,
      iconInitialsColor: values.initialsColor,
      iconPlacement: values.iconPlacement,
    }),
    previewItem: values => <IconPreviewItem values={values} />,
  })

  const {
    embroidery: {
      modal: { forms: fixtures },
    },
  } = useTranslation()

  const { isB2bEmbroidery, productCategoryId } = useEmbroidery()

  const iconPlacements = useValidPlacementOptionsForLegacyFlow(
    productEmbroideryConfig?.options.icon?.placements
  )

  const selectedIconImage = useStoreState(form, s => s.values.iconImage)
  const initials = useStoreState(form, s => s.values.initials)
  const initialsColor = useStoreState(form, s => s.values.initialsColor)
  const initialsTouched = useStoreState(form, () => form.getFieldTouched('initials'))
  const initialsError = useStoreState(form, () => form.getError('initials'))
  const initialsColorTouched = useStoreState(form, () => form.getFieldTouched('initialsColor'))
  const initialsColorError = useStoreState(form, () => form.getError('initialsColor'))

  /**
   * This is a temporary override to hide initials from
   * underscrub embroidery. This will become obsolete with
   * embroidery multiplacement. Delete this in ticket LFE-98
   */
  const disableInitialOverride =
    productCategoryId === 'Underscrub' ? fixtures.iconOption.iconInitialsUnavailable : null

  let disabledInitialsText: string | null | undefined =
    selectedIconImage?.group.disabledInitialsText ?? disableInitialOverride
  const initialsDisabled = Boolean(disabledInitialsText)

  // When switching to an icon group that disables initials, clear any previously typed value
  // so it doesn't persist in the store and get submitted with an icon that doesn't support it.
  React.useEffect(() => {
    if (initialsDisabled) {
      form.setValue('initials', '')
    }
  }, [initialsDisabled, form])

  const handleSubmit = React.useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      void form.submit()
    },
    [form]
  )

  return (
    <FormBody onSubmit={handleSubmit}>
      <IconSelectorForm form={form} labelId='select-icon-for-icon-only' />
      {initialsDisabled ? null : (
        <>
          <EmbroideryOptionWrapper>
            <Label>{fixtures.iconOption.initialsLabel}</Label>
            <EmbroideryTextInput
              errorMessage={initialsTouched ? initialsError ?? undefined : undefined}
              maxCharacterCount={initialsMaxChars}
              name='initials'
              onChange={e => form.setValue('initials', e.target.value)}
              onInput={transformInitialsInput}
              placeholder={disabledInitialsText ?? fixtures.iconOption.initialsPlaceholder}
              value={initials}
            />
          </EmbroideryOptionWrapper>

          <EmbroideryOptionWrapper>
            <ColorSelector
              errorMessage={initialsColorTouched ? initialsColorError ?? undefined : undefined}
              onSelectColor={color => form.setValue('initialsColor', color)}
              selectedColor={initialsColor}
              label={fixtures.iconOption.initialsColorLabel}
              // Temporary unblocking of colors for DTC. Remove this in LFE-187
              colorRestrictions={!isB2bEmbroidery ? [] : productEmbroideryConfig?.colorRestrictions}
              productColorHex={productColorHex}
            />
          </EmbroideryOptionWrapper>
        </>
      )}

      <IconPlacement
        labelId='icon-placement-for-icon-only'
        iconPlacements={iconPlacements}
        form={form}
        kind={selectedFormKind}
      />

      <IconSubmissionButtons>
        <StyledHr />
        <SubmissionButtons
          displayPrice={embroideryPriceRangeByType.ICON?.effectivePrice.displayValue}
          withPricing={withPricing}
          onReset={() => {
            resetForm()
          }}
        />
      </IconSubmissionButtons>
    </FormBody>
  )
}

const transformInitialsInput = (event: React.FormEvent<HTMLInputElement>) => {
  event.currentTarget.value = ('' + event.currentTarget.value).toUpperCase()
}

const IconSubmissionButtons = styled(EmbroideryOptionWrapper)`
  margin-bottom: ${o => o.theme.spacing(6)};
`
